/**
 * Quill AI Styles — co-located typed client for
 * `/api/admin/quill/ai-styles/*` (quill-admin-backend `aiStyles.js`).
 *
 * Follows the newer co-located pattern used by dimensions/api.ts,
 * photo-memories/api.ts and operations/api.ts: the `request` seam is replicated
 * here (the shared helpers in services/quillAdminService.ts are module-private —
 * only `QuillApiError` is exported) rather than editing the shared service.
 *
 * Error codes worth naming, both raised by the toggle route:
 *   409 MAX_ACTIVE_REACHED — a catalogue may have at most four active styles,
 *       because the app renders them in a 2×2 grid.
 *   400 HONESTY_GATE      — the row has no `ls_style_key`, so LifeScribe's
 *       transformer could not resolve it. Offering it would be a lie.
 * Both are surfaced in plain language by AIStylesPage rather than as raw codes.
 *
 * `list()` returns inactive AND withheld rows on purpose: this admin exists to
 * show the truth about the catalogue, including the styles members can't get.
 */

import { QuillApiError } from '../../../services/quillAdminService';
import { DEFAULT_USAGE_WINDOW_DAYS, KEY_FIELD, USAGE_CATEGORIES } from './constants';
import {
  CatalogueKey,
  CatalogueResult,
  ImportResult,
  PopularCategory,
  StyleRow,
  StyleUsageItem,
  StyleUsageResponse,
  StyleWritePayload,
  UsageCategory,
} from './types';

const BASE = '/api/admin/quill';
const SEG = '/ai-styles';
/**
 * A SIBLING of `/ai-styles` under the same mount, not a child of it — the read
 * side of migration 570 lives on its own router (quill-admin-backend
 * `styleUsage.js`) because it is read-only and the catalogue router is not.
 * Both reach quill-admin-backend the same way: the browser calls this
 * same-origin path, LifeScribe's admin-backend verifies the OAuth session and
 * forwards `/api/admin/quill/*` verbatim over VPC peering
 * (backend/routes/admin/quillAdminProxy.js), attaching the internal key.
 */
const USAGE_SEG = '/style-usage';

export { QuillApiError };

export const MAX_ACTIVE_REACHED = 'MAX_ACTIVE_REACHED';
export const HONESTY_GATE = 'HONESTY_GATE';
/** 503 from the usage route: 570 has not landed in this database. */
export const STYLE_USAGE_MIGRATION_NOT_APPLIED = 'STYLE_USAGE_MIGRATION_NOT_APPLIED';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }
  return (body ?? {}) as T;
}

/** The identity value of a row, resolved for the catalogue it came from. */
export function rowKey(row: StyleRow, catalogue: CatalogueKey): string {
  const field = KEY_FIELD[catalogue];
  const value = row[field];
  if (typeof value === 'string' && value.length > 0) return value;
  // Defensive: a row that arrived under a different key column still needs a
  // stable identity for React keys and for the PUT/DELETE path.
  return String(row.genre_key || row.author_key || row.style_key || row.display_name);
}

/**
 * Create bodies carry the identity under the catalogue's own column name
 * (`genre_key` / `author_key` / `style_key`); edits address it in the URL.
 */
function toCreateBody(catalogue: CatalogueKey, payload: StyleWritePayload) {
  const { key, ...rest } = payload;
  return { ...rest, [KEY_FIELD[catalogue]]: key };
}

export const aiStylesApi = {
  /** GET /:catalogue → `{ success, data: { rows, active_count } }`. */
  async list(catalogue: CatalogueKey): Promise<CatalogueResult> {
    const body = await request<{ data?: CatalogueResult }>(`${SEG}/${catalogue}`);
    const data = body.data ?? ({} as CatalogueResult);
    return {
      rows: Array.isArray(data.rows) ? data.rows : [],
      active_count: Number(data.active_count ?? 0),
    };
  },

  /** POST /:catalogue — create in the CURRENTLY OPEN catalogue. */
  async create(catalogue: CatalogueKey, payload: StyleWritePayload): Promise<StyleRow | undefined> {
    const body = await request<{ data?: StyleRow }>(`${SEG}/${catalogue}`, {
      method: 'POST',
      body: JSON.stringify(toCreateBody(catalogue, payload)),
    });
    return body.data;
  },

  /** PUT /:catalogue/:key — full edit, including the illustration prompt fields. */
  async update(
    catalogue: CatalogueKey,
    key: string,
    payload: StyleWritePayload
  ): Promise<StyleRow | undefined> {
    const { key: _ignored, ...rest } = payload;
    const body = await request<{ data?: StyleRow }>(
      `${SEG}/${catalogue}/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(rest) }
    );
    return body.data;
  },

  /** PATCH /:catalogue/:key/toggle — 409 MAX_ACTIVE_REACHED / 400 HONESTY_GATE. */
  async toggle(
    catalogue: CatalogueKey,
    key: string,
    isActive: boolean
  ): Promise<StyleRow | undefined> {
    const body = await request<{ data?: StyleRow }>(
      `${SEG}/${catalogue}/${encodeURIComponent(key)}/toggle`,
      { method: 'PATCH', body: JSON.stringify({ is_active: isActive }) }
    );
    return body.data;
  },

  /** DELETE /:catalogue/:key — SOFT delete; the row stays in the DB. */
  async remove(catalogue: CatalogueKey, key: string): Promise<void> {
    await request<unknown>(`${SEG}/${catalogue}/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  },

  /** PUT /reorder/:catalogue — `{ items: [{ key, sort_order }] }`. */
  async reorder(
    catalogue: CatalogueKey,
    items: Array<{ key: string; sort_order: number }>
  ): Promise<void> {
    await request<unknown>(`${SEG}/reorder/${catalogue}`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  },

  /** GET /popular-category — which catalogue wears the app's "Most Popular" ribbon. */
  async getPopularCategory(): Promise<PopularCategory> {
    const body = await request<{ data?: Partial<PopularCategory> } & Partial<PopularCategory>>(
      `${SEG}/popular-category`
    );
    const data = body.data ?? body;
    return {
      writing: data.writing || 'genre',
      illustration: data.illustration || 'style',
    };
  },

  /** PATCH /popular-category — partial `{ writing? , illustration? }`. */
  async setPopularCategory(patch: Partial<PopularCategory>): Promise<PopularCategory | undefined> {
    const body = await request<{ data?: PopularCategory }>(`${SEG}/popular-category`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return body.data;
  },

  /** POST /import-from-lifescribe — pulls LifeScribe's author catalogue across. */
  async importFromLifeScribe(): Promise<ImportResult> {
    const body = await request<{ data?: ImportResult } & ImportResult>(
      `${SEG}/import-from-lifescribe`,
      { method: 'POST' }
    );
    return body.data ?? body ?? {};
  },
};

/** One usage row, coerced but never invented. */
function shapeUsageItem(raw: unknown): StyleUsageItem {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    style_key: String(row.style_key ?? ''),
    ls_style_keys: Array.isArray(row.ls_style_keys) ? row.ls_style_keys.map(String) : [],
    total: Number(row.total ?? 0),
    window_count: Number(row.window_count ?? 0),
    first_seen: typeof row.first_seen === 'string' ? row.first_seen : null,
    last_seen: typeof row.last_seen === 'string' ? row.last_seen : null,
  };
}

export const styleUsageApi = {
  /**
   * GET /style-usage?days=N → the whole rollup.
   *
   * The envelope is flat (no `data` wrapper). Two things are deliberately NOT
   * normalised away:
   *   • `since: null` is preserved as null — it is the difference between "not
   *     recorded yet" and "recorded, and the count is zero".
   *   • a category the response omits stays OMITTED. Filling it with `[]` here
   *     would turn "not asked" into "asked and found nothing", and filling a
   *     style with 0 would turn "no rows" into a measurement.
   */
  async get(days: number = DEFAULT_USAGE_WINDOW_DAYS): Promise<StyleUsageResponse> {
    const body = await request<Record<string, unknown>>(
      `${USAGE_SEG}?days=${encodeURIComponent(String(days))}`
    );

    const rawCategories = (body.categories ?? {}) as Record<string, unknown>;
    const categories: Partial<Record<UsageCategory, StyleUsageItem[]>> = {};
    USAGE_CATEGORIES.forEach((category) => {
      const list = rawCategories[category];
      if (!Array.isArray(list)) return;
      categories[category] = list.map(shapeUsageItem);
    });

    const window = (body.window ?? {}) as Record<string, unknown>;
    return {
      since: typeof body.since === 'string' && body.since ? body.since : null,
      window: {
        days: Number(window.days ?? days),
        start: typeof window.start === 'string' ? window.start : '',
      },
      total: Number(body.total ?? 0),
      window_total: Number(body.window_total ?? 0),
      categories,
    };
  },
};

export default aiStylesApi;
